
// The read channel (wiki/feature-frontend-cqrs-dataflow.md §2): hydrate, then
// refetch on each matching push. A guest hydrates but never subscribes.
import { isAuthenticated } from '$lib/stores/auth';
import {
  adminReadModelsHub,
  readModelsHub,
  type ReadModelChange,
  type ReadModelsHub,
} from './readModelsHub';

export type IsAuthenticatedGate = () => boolean;

export type AuthChangeWatch = (cb: (authenticated: boolean) => void) => () => void;

const defaultOnAuthChange: AuthChangeWatch = (cb) => isAuthenticated.subscribe(cb);

export type LiveReadModelOptions<T> = {
  model: string;
  load: () => Promise<T>;
  /** A value the app already holds, published instead of the first load(). */
  initialValue?: T;
  matchId?: (entityId: string) => boolean;
  /** Admin cross-user watch of one entity, on the admin hub. */
  watchEntityId?: string;
  coalesceMs?: number;
  hub?: ReadModelsHub;
  isAuthenticated?: IsAuthenticatedGate;
  onAuthChange?: AuthChangeWatch;
};

export type RefetchOptions<T> = {
  retryAfterMs?: number;
  settled?: (value: T) => boolean;
};

export class LiveReadModel<T> {
  #data = $state<T | null>(null);
  #hydrated = $state(false);

  readonly #model: string;
  readonly #load: () => Promise<T>;
  readonly #initialValue?: T;
  readonly #matchId?: (entityId: string) => boolean;
  readonly #watchEntityId?: string;
  readonly #coalesceMs: number;
  readonly #hub: ReadModelsHub;
  readonly #onAuthChange: AuthChangeWatch;

  #unsubscribe: (() => void) | null = null;
  #unregisterResync: (() => void) | null = null;
  #unwatchAuth: (() => void) | null = null;
  #coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  #started = false;
  #disposed = false;

  constructor(opts: LiveReadModelOptions<T>) {
    this.#model = opts.model;
    this.#load = opts.load;
    this.#initialValue = opts.initialValue;
    this.#matchId = opts.matchId;
    this.#watchEntityId = opts.watchEntityId;
    this.#coalesceMs = opts.coalesceMs ?? 250;
    this.#hub = opts.hub ?? (opts.watchEntityId ? adminReadModelsHub : readModelsHub);
    const gate = opts.isAuthenticated;
    this.#onAuthChange =
      opts.onAuthChange ??
      (gate
        ? (cb) => {
            cb(gate());
            return () => {};
          }
        : defaultOnAuthChange);
  }

  get data(): T | null {
    return this.#data;
  }

  get hydrated(): boolean {
    return this.#hydrated;
  }

  async start(): Promise<void> {
    if (this.#started) return;
    this.#started = true;
    this.#disposed = false;

    if (this.#initialValue !== undefined) {
      this.#data = this.#initialValue;
      this.#hydrated = true;
    } else {
      await this.#hydrate();
    }

    if (!this.#started) return;

    // Live, not one-shot: auth can resolve after a hard load's hydrate, and a late
    // open re-hydrates once for the pushes it missed.
    let initialAuthEval = true;
    this.#unwatchAuth = this.#onAuthChange((authed) => {
      if (!authed || !this.#started || this.#unsubscribe) return;
      this.#openChannel();
      if (!initialAuthEval) this.#scheduleRefetch();
    });
    initialAuthEval = false;
  }

  #openChannel(): void {
    this.#unsubscribe = this.#watchEntityId
      ? this.#hub.watchEntity(this.#model, this.#watchEntityId, (change) => this.#onPush(change))
      : this.#hub.subscribe(this.#model, (change) => this.#onPush(change));
    this.#unregisterResync = this.#hub.onResync(() => this.#scheduleRefetch());
  }

  stop(): void {
    this.#started = false;
    this.#disposed = true;
    this.#hydrated = false;
    if (this.#coalesceTimer) {
      clearTimeout(this.#coalesceTimer);
      this.#coalesceTimer = null;
    }
    if (this.#unwatchAuth) {
      this.#unwatchAuth();
      this.#unwatchAuth = null;
    }
    if (this.#unsubscribe) {
      this.#unsubscribe();
      this.#unsubscribe = null;
    }
    if (this.#unregisterResync) {
      this.#unregisterResync();
      this.#unregisterResync = null;
    }
  }

  async refetch(opts: RefetchOptions<T> = {}): Promise<void> {
    await this.#hydrate();
    const { retryAfterMs, settled } = opts;
    if (retryAfterMs && retryAfterMs > 0) {
      const value = this.#data;
      const isSettled = settled ? value !== null && settled(value) : true;
      if (!isSettled) {
        await new Promise((resolve) => setTimeout(resolve, retryAfterMs));
        if (!this.#disposed) await this.#hydrate();
      }
    }
  }

  async #hydrate(): Promise<void> {
    const value = await this.#load();
    this.#data = value;
    this.#hydrated = true;
  }

  #onPush(change: ReadModelChange): void {
    if (this.#matchId && !this.#matchId(change.entityId)) return;
    this.#scheduleRefetch();
  }

  #scheduleRefetch(): void {
    if (this.#coalesceTimer) clearTimeout(this.#coalesceTimer);
    // A background refetch has nobody to tell; the next push or resync retries it.
    this.#coalesceTimer = setTimeout(() => {
      this.#coalesceTimer = null;
      this.#hydrate().catch((err) => console.warn(`[liveReadModel ${this.#model}] refetch failed`, err));
    }, this.#coalesceMs);
  }
}

export function liveReadModel<T>(opts: LiveReadModelOptions<T>): LiveReadModel<T> {
  return new LiveReadModel<T>(opts);
}
